import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_NAME } from '../../constants';
import { useAuth } from '../../hooks/useAuth';
import { usePlan } from '../../hooks/usePlan';
import { useToast } from '../ui/Toast';
import { PricingHero } from '../pricing/PricingHero';
import { PricingGrid } from '../pricing/PricingGrid';
import type { BillingCycle, TierId } from '../pricing/tiersData';

export const OnboardingPlans: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { upgrade } = usePlan();
  const { showToast } = useToast();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  const firstName = user?.full_name?.split(' ')[0] || '';

  const handleSelect = async (tier: TierId) => {
    if (tier === 'free') {
      navigate('/dashboard');
      return;
    }
    const result = await upgrade(tier, billingCycle);
    if (result.ok) return;
    const errCode = result.error;
    console.error('Onboarding upgrade error:', errCode);
    showToast(
      'error',
      errCode === 'endpoint_unavailable'
        ? 'Checkout temporariamente indisponível. Você pode continuar grátis e assinar depois.'
        : 'Não foi possível iniciar o checkout. Você pode continuar grátis e assinar depois.',
    );
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-neutral-950 relative overflow-hidden">
      <Helmet>
        <title>{`Escolha seu plano — ${SITE_NAME}`}</title>
      </Helmet>

      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px]"
          style={{ animation: 'floatBlob 15s ease-in-out infinite' }}
        />
        <div
          className="absolute bottom-[-20%] left-[-5%] w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[80px]"
          style={{ animation: 'floatBlob 20s ease-in-out infinite reverse' }}
        />
      </div>

      <main className="relative z-10 px-4 sm:px-6 pt-10 pb-16">
        <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            {firstName ? `Bem-vindo, ${firstName}!` : 'Bem-vindo!'}
          </h1>
          <p className="text-neutral-300 text-base sm:text-lg">
            Escolha o plano ideal pra começar. Você pode trocar a qualquer momento.
          </p>
        </div>

        <PricingHero billingCycle={billingCycle} onCycleChange={setBillingCycle} />
        <PricingGrid billingCycle={billingCycle} currentTier="free" onSelect={handleSelect} />

        <div className="text-center mt-8">
          <button
            type="button"
            onClick={handleSkip}
            className="inline-flex items-center justify-center min-h-[44px] px-3 py-2 sm:min-h-0 sm:p-0 text-sm text-neutral-500 hover:text-neutral-300 transition-colors underline underline-offset-4"
          >
            Decidir depois
          </button>
        </div>
      </main>
    </div>
  );
};
